import { useEffect, useState } from 'react';
import type { DashboardState } from '../lib/ws';
import { formatUsd } from '../lib/rtl';
import { apiUrl } from '../lib/api';

export function Admin() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [state, setState] = useState<DashboardState | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    setLoginError(null);
    const r = await fetch(apiUrl('/api/admin/login'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!r.ok) { setLoginError('Wrong password'); return; }
    setAuthed(true);
  }

  async function call(action: string, path: string, body?: unknown) {
    setBusyKey(action);
    try {
      const r = await fetch(apiUrl(`/api/admin${path}`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) throw new Error(`${r.status}`);
      await refresh();
    } finally {
      setBusyKey(null);
    }
  }

  async function refresh() {
    const r = await fetch(apiUrl('/api/admin/state'), { credentials: 'include' });
    if (r.ok) setState(await r.json() as DashboardState);
  }

  async function exportJson() {
    window.location.href = apiUrl('/api/admin/export');
  }

  async function revealPassword() {
    setBusyKey('reveal-pw');
    try {
      const r = await fetch(apiUrl('/api/admin/password'), { credentials: 'include' });
      if (r.ok) {
        const j = await r.json() as { password: string };
        setRevealedPassword(j.password);
      }
    } finally {
      setBusyKey(null);
    }
  }

  const [connTest, setConnTest] = useState<null | { ok: boolean; checks: Record<string, { ok: boolean; ms: number; detail?: string }> }>(null);
  async function runConnectionTest() {
    setBusyKey('conn-test');
    setConnTest(null);
    try {
      const r = await fetch(apiUrl('/api/admin/connection-test'), { credentials: 'include' });
      setConnTest(await r.json());
    } finally {
      setBusyKey(null);
    }
  }

  type FeedbackItem = {
    id: string;
    ts: number;
    sid: string;
    display_name: string;
    question: string;
    answer: string;
    comment: string;
    reason: string | null;
  };
  const [feedback, setFeedback] = useState<FeedbackItem[] | null>(null);

  async function loadFeedback() {
    setBusyKey('load-feedback');
    try {
      const r = await fetch(apiUrl('/api/admin/feedback'), { credentials: 'include' });
      if (r.ok) setFeedback((await r.json()) as FeedbackItem[]);
    } finally {
      setBusyKey(null);
    }
  }

  function downloadFeedback() {
    if (!feedback) return;
    const blob = new Blob([JSON.stringify(feedback, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marsad-etimad-reports-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    fetch(apiUrl('/api/admin/state'), { credentials: 'include' })
      .then((r) => { if (r.ok) { setAuthed(true); return r.json(); } return null; })
      .then((j) => { if (j) setState(j); })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    if (!authed) return;
    const interval = setInterval(refresh, 2000);
    return () => clearInterval(interval);
  }, [authed]);

  // ESC closes the QR modal
  useEffect(() => {
    if (!showQr) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowQr(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showQr]);

  if (!authed) {
    return (
      <main className="min-h-full grid place-items-center p-6 bg-tatweer-light">
        <form onSubmit={login} className="max-w-sm w-full bg-white rounded-2xl shadow-card-lg ring-1 ring-tatweer-line p-8 space-y-5">
          <div className="flex items-center gap-3 pb-2 border-b border-tatweer-line">
            <div className="rounded-lg bg-tatweer-navy p-2.5">
              <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-9 w-auto" />
            </div>
            <div>
              <h1 className="text-base font-bold text-tatweer-navy">Admin Console</h1>
              <p className="text-xs text-tatweer-muted">Workshop control panel</p>
            </div>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="password"
            className="block w-full rounded-xl bg-white ring-1 ring-tatweer-line px-4 py-3 text-tatweer-body placeholder:text-tatweer-muted focus:outline-none focus:ring-2 focus:ring-tatweer-navy"
            dir="ltr"
            autoFocus
          />
          {loginError && <div className="text-rose-600 text-sm">{loginError}</div>}
          <button type="submit" className="w-full rounded-xl bg-tatweer-navy hover:bg-tatweer-navy-700 text-white font-semibold py-3 transition">
            Unlock
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-full bg-tatweer-light" dir="ltr">
      {/* Header */}
      <header className="bg-tatweer-navy w-full">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-12 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">Admin · Marsad Etimad</div>
              <div className="text-lg font-bold text-white leading-tight mt-0.5">Workshop control panel</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowQr(true)}
              className="text-xs font-semibold text-tatweer-navy bg-white px-4 py-2 rounded-full hover:bg-tatweer-light transition"
            >
              📱 Show workshop QR
            </button>
            <a href="/dashboard" target="_blank" className="text-xs text-white/80 hover:text-white px-3 py-2 rounded-full ring-1 ring-white/20 hover:ring-white/40 transition">
              Open dashboard
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        <p className="text-tatweer-body-soft text-sm mb-6">
          State refreshes every 2 seconds. Changes apply immediately on the next query; in-flight streams complete on previous settings.
        </p>

        {state?.fallback_reason && (
          <div className="mb-4 rounded-2xl bg-amber-50 ring-1 ring-tatweer-gold/40 p-4 text-sm text-tatweer-body">
            <span className="font-bold text-tatweer-gold">⚠ Auto-fallback fired:</span> {state.fallback_reason}
          </div>
        )}

        {(state?.testing_mode || state?.allow_off_topic) && (
          <div className="mb-4 rounded-2xl bg-rose-50 ring-2 ring-rose-400 p-4 text-sm text-rose-900 font-semibold">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">🚧</span>
              <span className="uppercase tracking-wider">Live safety rails are off</span>
            </div>
            <ul className="list-disc list-inside text-xs font-normal">
              {state?.testing_mode && <li>Testing Mode: Turnstile + per-session/per-IP rate limits are bypassed</li>}
              {state?.allow_off_topic && <li>Allow off-topic: scope classifier is bypassed (bot answers anything)</li>}
            </ul>
            <p className="text-xs font-normal mt-1">Turn these OFF before the live workshop.</p>
          </div>
        )}

        {/* Top-line metrics */}
        {state && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <InfoTile label="Connected" value={state.connected} />
            <InfoTile label="Queries" value={state.total_queries} />
            <InfoTile label="Cache hits" value={`${state.cache_hits} / ${state.cache_size}`} />
            <InfoTile label="Predictions" value={state.predictions_count} />
            <InfoTile label="Cost so far" value={formatUsd(state.budget_used_usd)} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Flow control">
            <Row
              label={state?.paused ? '▶ Resume incoming queries' : '⏸ Pause new queries (testing freeze)'}
              action={() => call('pause', '/pause', { paused: !state?.paused })}
              busy={busyKey === 'pause'}
              tone={state?.paused ? 'ok' : 'warn'}
            />
            <Row
              label="🧹 Reset everything for live workshop"
              hint="Wipes test queries, predictions, feed, and cost counters. Keeps API keys."
              action={() => confirm('Reset ALL counters + feed + predictions? This is what you press when test data is contaminating the workshop.') && call('reset', '/reset')}
              busy={busyKey === 'reset'}
              tone="danger"
            />
            <Row
              label={state?.reveal_mode ? '🎬 End reveal mode' : '🎬 Trigger reveal moment'}
              action={() => call('reveal', '/reveal', { reveal_mode: !state?.reveal_mode })}
              busy={busyKey === 'reveal'}
              tone={state?.reveal_mode ? 'warn' : 'ok'}
            />
          </Section>

          <Section title="Models">
            <Row
              label={`Provider: ${state?.provider ?? '—'} → switch to ${state?.provider === 'anthropic' ? 'OpenAI' : 'Anthropic'}`}
              action={() => confirm(`Switch provider to ${state?.provider === 'anthropic' ? 'OpenAI (GPT-5 mini)' : 'Anthropic (Claude)'}?`) && call('provider', '/provider', { provider: state?.provider === 'anthropic' ? 'openai' : 'anthropic' })}
              busy={busyKey === 'provider'}
            />
            <Row
              label={`Mode B model: ${state?.mode_b_model === 'opus' ? 'Opus 4.7' : 'Sonnet 4.6'} → switch to ${state?.mode_b_model === 'opus' ? 'Sonnet 4.6' : 'Opus 4.7'}`}
              action={() => call('mode-b-model', '/mode-b-model', { model: state?.mode_b_model === 'opus' ? 'sonnet' : 'opus' })}
              busy={busyKey === 'mode-b-model'}
            />
          </Section>

          <Section title="Access controls">
            <Row
              label={`🧪 Testing Mode: ${state?.testing_mode ? 'ON' : 'off'} → ${state?.testing_mode ? 'TURN OFF' : 'TURN ON'}`}
              hint={state?.testing_mode
                ? '⚠ All rate limits + Turnstile are bypassed. Pre-workshop only — turn OFF before live.'
                : 'Bypasses Turnstile + per-session/per-IP rate limits. Use for warm-cache, smoke tests, beta abuse runs.'}
              action={() => call('testing-mode', '/testing-mode', { enabled: !state?.testing_mode })}
              busy={busyKey === 'testing-mode'}
              tone={state?.testing_mode ? 'danger' : undefined}
            />
            <Row
              label={`🌐 Allow off-topic answers: ${state?.allow_off_topic ? 'ON' : 'off'} → ${state?.allow_off_topic ? 'TURN OFF' : 'TURN ON'}`}
              hint={state?.allow_off_topic
                ? '⚠ Scope classifier bypassed — bot answers ANY question. Workshop voice may drift. Turn OFF before live.'
                : 'When ON: bot tries to answer everything (no out-of-scope refusals). Use for exploratory beta testing only.'}
              action={() => call('allow-off-topic', '/allow-off-topic', { enabled: !state?.allow_off_topic })}
              busy={busyKey === 'allow-off-topic'}
              tone={state?.allow_off_topic ? 'danger' : undefined}
            />
            <Row
              label={`Turnstile (CAPTCHA on onboarding): ${state?.turnstile_enabled === false ? 'OFF' : 'ON'} → ${state?.turnstile_enabled === false ? 'ENABLE' : 'DISABLE'}`}
              hint={state?.turnstile_enabled === false
                ? '⚠ Bots can sign up freely. Re-enable before going live!'
                : 'Disable only for stress tests or if the widget jams during the workshop.'}
              action={() => call('turnstile', '/turnstile', { enabled: !(state?.turnstile_enabled !== false) })}
              busy={busyKey === 'turnstile'}
              tone={state?.turnstile_enabled === false ? 'danger' : 'ok'}
            />
            <Row
              label={revealedPassword ? `Admin password: ${revealedPassword}` : '🔑 Reveal admin password'}
              hint="Used at /admin login. Rotate by editing apps/worker/.dev.vars + redeploying."
              action={revealPassword}
              busy={busyKey === 'reveal-pw'}
            />
          </Section>

          <Section title="Exports">
            <Row label="📥 Download session JSON" action={exportJson} busy={busyKey === 'export'} />
          </Section>

          <Section title="Safety levers">
            <p className="text-[11px] text-tatweer-muted mb-2 leading-relaxed">
              Adjust mid-workshop if you see strain. All take effect on the next query.
            </p>

            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-tatweer-body-soft">
                <span>Per-session rate ({state?.per_session_rate_per_min ?? 6}/min)</span>
                <span className="text-tatweer-muted">Cloudflare binding is the floor</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[2, 4, 6].map((n) => (
                  <button
                    key={n}
                    onClick={() => call('rate-limit', '/rate-limit', { per_min: n })}
                    disabled={busyKey === 'rate-limit'}
                    className={`rounded-lg px-3 py-2 text-sm ring-1 transition ${state?.per_session_rate_per_min === n
                      ? 'bg-tatweer-navy text-white ring-tatweer-navy'
                      : 'bg-white ring-tatweer-line hover:bg-tatweer-light text-tatweer-body'}`}
                  >
                    {n}/min
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1 pt-3">
              <div className="flex items-center justify-between text-xs text-tatweer-body-soft">
                <span>Throttle delay ({state?.throttle_ms ?? 0}ms)</span>
                <span className="text-tatweer-muted">smooths spikes; no errors</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[0, 500, 1500].map((ms) => (
                  <button
                    key={ms}
                    onClick={() => call('throttle', '/throttle', { throttle_ms: ms })}
                    disabled={busyKey === 'throttle'}
                    className={`rounded-lg px-3 py-2 text-sm ring-1 transition ${state?.throttle_ms === ms
                      ? 'bg-tatweer-navy text-white ring-tatweer-navy'
                      : 'bg-white ring-tatweer-line hover:bg-tatweer-light text-tatweer-body'}`}
                  >
                    {ms === 0 ? 'off' : `+${ms}ms`}
                  </button>
                ))}
              </div>
            </div>

            <Row
              label={`Auto-fallback to OpenAI on 429s: ${state?.auto_fallback_enabled ? 'ON' : 'off'}`}
              action={() => call('auto-fallback', '/auto-fallback', { enabled: !state?.auto_fallback_enabled })}
              busy={busyKey === 'auto-fallback'}
              tone={state?.auto_fallback_enabled ? 'ok' : undefined}
            />
          </Section>

          <Section title="User reports">
            <p className="text-[11px] text-tatweer-muted mb-2 leading-relaxed">
              Wrong-answer reports submitted from chat bubbles. Cleared on Reset.
            </p>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-3xl font-bold tabular-nums text-tatweer-navy">{state?.feedback_count ?? 0}</span>
              <span className="text-xs text-tatweer-muted uppercase tracking-wider">submitted</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Row label="📥 Load latest reports" action={loadFeedback} busy={busyKey === 'load-feedback'} />
              <Row label="⬇ Download all (JSON)" action={downloadFeedback} busy={false} />
            </div>
            {feedback && feedback.length === 0 && (
              <p className="mt-3 text-xs text-tatweer-muted">No reports yet.</p>
            )}
            {feedback && feedback.length > 0 && (
              <div className="mt-3 max-h-96 overflow-y-auto tatweer-scroll space-y-2 pr-1">
                {[...feedback].reverse().slice(0, 20).map((f) => (
                  <div key={f.id} className="rounded-xl bg-tatweer-light ring-1 ring-tatweer-line p-3 space-y-1">
                    <div className="flex items-center justify-between gap-2 text-[10px] text-tatweer-muted uppercase tracking-wider">
                      <span>{f.display_name}{f.reason ? ` · ${f.reason}` : ''}</span>
                      <span className="tabular-nums">{new Date(f.ts).toLocaleString()}</span>
                    </div>
                    <div className="text-xs text-tatweer-body-soft truncate" dir="auto">Q: {f.question}</div>
                    <div className="text-sm text-tatweer-body" dir="auto">{f.comment}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Health">
            <Row label="🩺 Run connection test (Vectorize · Anthropic · OpenAI)" action={runConnectionTest} busy={busyKey === 'conn-test'} />
            {connTest && (
              <div className={`mt-2 rounded-xl ring-1 p-3 text-xs space-y-1 ${connTest.ok ? 'bg-emerald-50 ring-tatweer-green/40' : 'bg-rose-50 ring-rose-300/40'}`}>
                {Object.entries(connTest.checks).map(([name, r]) => (
                  <div key={name} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${r.ok ? 'bg-tatweer-green' : 'bg-rose-500'}`} />
                      <span className="font-medium text-tatweer-body">{name}</span>
                    </span>
                    <span className="tabular-nums text-tatweer-muted">
                      {r.ok ? `${r.ms}ms` : `FAIL · ${r.detail ?? ''}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {showQr && <QrModal onClose={() => setShowQr(false)} />}
    </main>
  );
}

function QrModal({ onClose }: { onClose: () => void }) {
  // The workshop URL is whatever this page is on (so it stays correct if the
  // domain changes). The QR is generated fresh from the current origin.
  const [origin, setOrigin] = useState('');
  useEffect(() => { setOrigin(window.location.origin); }, []);
  const url = origin ? `${origin}/` : '';
  const qrSrc = origin
    ? `https://api.qrserver.com/v1/create-qr-code/?size=720x720&margin=24&qzone=4&data=${encodeURIComponent(url)}&color=19355C&bgcolor=FFFFFF`
    : '';

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-tatweer-navy/95 backdrop-blur"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative bg-white rounded-3xl shadow-card-lg p-10 max-w-3xl w-full mx-4 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-tatweer-muted hover:text-tatweer-body text-2xl leading-none"
        >
          ×
        </button>
        <div className="flex items-center justify-center gap-3 pb-4 border-b border-tatweer-line mb-6">
          <div className="rounded-lg bg-tatweer-navy p-2.5">
            <img src="/brand/tatweer-white.svg" alt="Tatweer" className="h-10 w-auto" />
          </div>
          <div className="text-start">
            <div className="text-[10px] uppercase tracking-[0.3em] text-tatweer-muted">Scan to join</div>
            <div className="text-2xl font-bold text-tatweer-navy">مرصد اعتماد</div>
          </div>
        </div>

        {qrSrc ? (
          <img
            src={qrSrc}
            alt="QR code"
            className="mx-auto rounded-2xl ring-1 ring-tatweer-line p-2"
            style={{ width: 'min(72vmin, 720px)', height: 'min(72vmin, 720px)' }}
          />
        ) : (
          <div className="bg-tatweer-light rounded-2xl mx-auto" style={{ width: 'min(72vmin, 720px)', height: 'min(72vmin, 720px)' }} />
        )}

        <div className="mt-6 text-2xl font-mono text-tatweer-navy break-all">{url}</div>
        <div className="mt-3 text-sm text-tatweer-body-soft">
          Press <kbd className="px-1.5 py-0.5 ring-1 ring-tatweer-line rounded text-xs">Esc</kbd> or click outside to close
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white ring-1 ring-tatweer-line p-4 shadow-card">
      <div className="text-[10px] text-tatweer-muted uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-bold tabular-nums text-tatweer-navy">{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-white ring-1 ring-tatweer-line p-5 space-y-3 shadow-card">
      <h2 className="text-xs text-tatweer-navy uppercase tracking-wider font-bold">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, hint, action, busy, tone }: { label: string; hint?: string; action: () => void; busy: boolean; tone?: 'ok' | 'warn' | 'danger' }) {
  const toneClass = tone === 'danger'
    ? 'bg-rose-50 hover:bg-rose-100 ring-rose-300 text-rose-900'
    : tone === 'warn'
    ? 'bg-amber-50 hover:bg-amber-100 ring-tatweer-gold/40 text-tatweer-body'
    : tone === 'ok'
    ? 'bg-emerald-50 hover:bg-emerald-100 ring-tatweer-green/40 text-tatweer-body'
    : 'bg-tatweer-light hover:bg-white ring-tatweer-line text-tatweer-body';
  return (
    <div>
      <button
        onClick={action}
        disabled={busy}
        className={`w-full text-start rounded-xl ring-1 px-4 py-3 transition disabled:opacity-50 font-medium ${toneClass}`}
      >
        {busy ? '…' : label}
      </button>
      {hint && <p className="mt-1 text-[11px] text-tatweer-muted leading-relaxed px-1">{hint}</p>}
    </div>
  );
}
